import { useEffect, useRef, useState } from "react";
import { useCart } from "@/lib/cart";
import { config, placeOrder, sendOrderEmail } from "@/lib/config";
import "./CartDrawer.css";

type Props = {
  open: boolean;
  onClose: () => void;
};

function parseDollars(price: string): number {
  return Number(price.replace(/[^0-9.]/g, "")) || 0;
}

function buildOrderBody(fields: {
  name: string;
  customerNumber: string;
  referralNumber: string;
  area: string;
  lines: string[];
  subtotal: string;
}) {
  const parts: string[] = [];
  if (fields.name) parts.push(`Name: ${fields.name}`);
  parts.push(`Customer #: ${fields.customerNumber}`);
  if (fields.referralNumber) parts.push(`Referral #: ${fields.referralNumber}`);
  parts.push(`Address: ${fields.area}`);
  parts.push("");
  parts.push("Order:");
  parts.push(...fields.lines);
  parts.push("");
  parts.push(`Subtotal: ${fields.subtotal}`);
  return parts.join("\n");
}

export function CartDrawer({ open, onClose }: Props) {
  const { items, totalItems, removeItem, setItemQty, clearCart } = useCart();
  const panelRef = useRef<HTMLDivElement>(null);

  const [name, setName] = useState("");
  const [customerNumber, setCustomerNumber] = useState("");
  const [referralNumber, setReferralNumber] = useState("");
  const [area, setArea] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [confirmedSummary, setConfirmedSummary] = useState("");

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  const subtotal = items.reduce((sum, i) => sum + parseDollars(i.tier.price) * i.qty, 0);
  const canCheckout =
    items.length > 0 && customerNumber.trim().length > 0 && area.trim().length > 0;

  const orderLines = items.map((i) => {
    const flavorPart = i.flavor ? ` [${i.flavor}]` : "";
    return `  ${i.qty}x ${i.name}${flavorPart} (${i.tier.qty}) @ ${i.tier.price}`;
  });
  const subtotalStr = `$${subtotal.toFixed(2)}`;

  const bodyFields = {
    name: name.trim(),
    customerNumber: customerNumber.trim(),
    referralNumber: referralNumber.trim(),
    area: area.trim(),
    lines: orderLines,
    subtotal: subtotalStr,
  };

  const orderBody = buildOrderBody(bodyFields);

  function handleCheckout() {
    setConfirmedSummary(orderBody);
    setConfirmed(true);
    sendOrderEmail(orderBody, customerNumber.trim());
    placeOrder(orderBody, customerNumber.trim());
    clearCart();
    setName("");
    setCustomerNumber("");
    setReferralNumber("");
    setArea("");
  }

  function handleDone() {
    setConfirmed(false);
    onClose();
  }

  if (confirmed) {
    return (
      <div className="cart-overlay" onClick={handleDone}>
        <div
          className="cart-drawer"
          role="dialog"
          aria-label="Order confirmed"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cart-drawer__confirm">
            <div className="cart-drawer__confirm-icon" aria-hidden="true">
              &#x2714;
            </div>
            <h2 className="cart-drawer__confirm-title">To Complete Your Order</h2>
            <p className="cart-drawer__confirm-text">
              Send the Order Detail in your messaging app. We&rsquo;ll confirm shortly.
            </p>
            <p className="cart-drawer__confirm-phone">
              Questions? Text{" "}
              <a href={`tel:${config.contactPhoneTel}`}>{config.contactPhoneDisplay}</a>
            </p>
            <details className="cart-drawer__confirm-details">
              <summary>Order summary</summary>
              <pre className="cart-drawer__confirm-pre">{confirmedSummary}</pre>
            </details>
            <button type="button" className="cart-drawer__confirm-btn" onClick={handleDone}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-overlay" onClick={onClose}>
      <div
        className="cart-drawer"
        ref={panelRef}
        role="dialog"
        aria-label="Your cart"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cart-drawer__header">
          <h2 className="cart-drawer__title">Your Cart</h2>
          <button
            type="button"
            className="cart-drawer__close"
            onClick={onClose}
            aria-label="Close cart"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        {items.length === 0 ? (
          <div className="cart-drawer__empty">
            <p>Your cart is empty.</p>
            <p>Browse the menu and add items to get started.</p>
          </div>
        ) : (
          <>
            <ul className="cart-drawer__items">
              {items.map((item) => (
                <li
                  key={`${item.slug}-${item.tier.qty}-${item.flavor ?? ""}`}
                  className="cart-item"
                >
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      alt=""
                      className="cart-item__img"
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  <div className="cart-item__info">
                    <span className="cart-item__name">{item.name}</span>
                    <span className="cart-item__tier">
                      {item.tier.qty} &mdash; {item.tier.price}
                    </span>
                    {item.flavor && <span className="cart-item__flavor">{item.flavor}</span>}
                  </div>
                  <div className="cart-item__controls">
                    <button
                      type="button"
                      className="cart-item__qty-btn"
                      aria-label="Decrease quantity"
                      onClick={() =>
                        setItemQty(item.slug, item.tier.qty, item.qty - 1, item.flavor)
                      }
                    >
                      &minus;
                    </button>
                    <span className="cart-item__qty">{item.qty}</span>
                    <button
                      type="button"
                      className="cart-item__qty-btn"
                      aria-label="Increase quantity"
                      onClick={() =>
                        setItemQty(item.slug, item.tier.qty, item.qty + 1, item.flavor)
                      }
                    >
                      +
                    </button>
                  </div>
                  <button
                    type="button"
                    className="cart-item__remove"
                    aria-label={`Remove ${item.name}`}
                    onClick={() => removeItem(item.slug, item.tier.qty, item.flavor)}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    >
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>

            <footer className="cart-drawer__footer">
              <div className="cart-drawer__fields">
                <div className="cart-field">
                  <label className="cart-field__label" htmlFor="cart-name">
                    Name <span className="cart-field__opt">(optional)</span>
                  </label>
                  <input
                    id="cart-name"
                    type="text"
                    className="cart-field__input"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                <div className="cart-field">
                  <label className="cart-field__label" htmlFor="cart-customer">
                    Customer # <span className="cart-field__req">*</span>
                  </label>
                  <input
                    id="cart-customer"
                    type="text"
                    className="cart-field__input"
                    placeholder="Your customer number"
                    value={customerNumber}
                    onChange={(e) => setCustomerNumber(e.target.value)}
                    required
                  />
                </div>

                <div className="cart-field">
                  <label className="cart-field__label" htmlFor="cart-referral">
                    Referral # <span className="cart-field__opt">(if applicable)</span>
                  </label>
                  <input
                    id="cart-referral"
                    type="text"
                    className="cart-field__input"
                    placeholder="Referral number"
                    value={referralNumber}
                    onChange={(e) => setReferralNumber(e.target.value)}
                  />
                </div>

                <div className="cart-field">
                  <label className="cart-field__label" htmlFor="cart-area">
                    Address / Cross Street <span className="cart-field__req">*</span>
                  </label>
                  <input
                    id="cart-area"
                    type="text"
                    className="cart-field__input"
                    placeholder="Address or nearest cross street"
                    value={area}
                    onChange={(e) => setArea(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="cart-drawer__summary">
                <span>
                  {totalItems} item{totalItems !== 1 ? "s" : ""}
                </span>
                <span className="cart-drawer__subtotal">{subtotalStr}</span>
              </div>

              {canCheckout ? (
                <button type="button" className="cart-drawer__checkout" onClick={handleCheckout}>
                  Place Order
                </button>
              ) : (
                <span
                  className="cart-drawer__checkout cart-drawer__checkout--disabled"
                  aria-disabled="true"
                >
                  Fill required fields to order
                </span>
              )}

              <button type="button" className="cart-drawer__clear" onClick={clearCart}>
                Clear cart
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
